
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card } from "@/components/ui/card";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { ListLoadingState } from "@/components/loading/ListLoadingState";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

type Profile = Database['public']['Tables']['profiles']['Row'] & {
  roles: Array<{
    role: Database['public']['Enums']['app_role']
  }>
};

export const AdminUsers = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const { data: users, isLoading, error } = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      try {
        // Get all profiles with a more efficient approach - single query
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('*');

        if (profilesError) {
          console.error("Error fetching profiles:", profilesError);
          throw profilesError;
        }

        // Fetch all user roles in a single query
        const { data: allRoles, error: rolesError } = await supabase
          .from('user_roles')
          .select('user_id, role');

        if (rolesError) {
          console.error("Error fetching user roles:", rolesError);
          throw rolesError;
        }

        // Map roles to profiles
        const usersWithRoles = profiles.map(profile => {
          const userRoles = allRoles.filter(role => role.user_id === profile.id);
          return {
            ...profile,
            roles: userRoles.map(r => ({ role: r.role }))
          };
        });

        return usersWithRoles as Profile[];
      } catch (error) {
        console.error("AdminUsers query error:", error);
        throw error;
      }
    }
  });

  const toggleChefRole = useMutation({
    mutationFn: async ({ userId, isChef }: { userId: string; isChef: boolean }) => {
      if (isChef) {
        // Remove chef role
        const { error } = await supabase
          .from('user_roles')
          .delete()
          .eq('user_id', userId)
          .eq('role', 'cook');
          
        if (error) throw error;
      } else {
        // Add chef role
        const { error } = await supabase
          .from('user_roles')
          .insert({ user_id: userId, role: 'cook' });
          
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast({
        title: "Success",
        description: "Chef status has been updated",
        key: `chef-status-update-${Date.now()}`
      });
    },
    onError: (error) => {
      console.error("Error toggling chef role:", error);
      toast({
        title: "Error",
        description: "Failed to update chef status",
        variant: "destructive",
        key: `chef-status-error-${Date.now()}`
      });
    },
  });

  // Display error state if query fails
  if (error) {
    return (
      <AdminLayout title="Users">
        <div className="p-4 border border-red-300 bg-red-50 rounded-md">
          <p className="text-red-700">Error loading users. Please try again later.</p>
          <p className="text-sm text-red-500">{String(error)}</p>
        </div>
      </AdminLayout>
    );
  }

  const foodieUsers = users?.filter(user => 
    user.roles.some(role => role.role === 'user') &&
    !user.roles.some(role => role.role === 'cook')
  );

  const chefUsers = users?.filter(user => 
    user.roles.some(role => role.role === 'cook')
  );

  const UserCard = ({ user, isChef }: { user: Profile; isChef: boolean }) => (
    <Card key={user.id} className="p-4">
      <div className="flex items-center gap-4">
        {user.image_url && (
          <img 
            src={user.image_url} 
            alt={`${user.first_name} ${user.last_name}`}
            className="w-12 h-12 rounded-full object-cover"
          />
        )}
        <div className="flex-1">
          <h3 className="font-medium">
            {user.first_name} {user.last_name}
          </h3>
          <p className="text-sm text-muted-foreground">
            {user.city}, {user.state}
          </p>
          <div className="flex gap-2 mt-1">
            {user.roles?.map((role) => (
              <span 
                key={role.role}
                className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full"
              >
                {role.role}
              </span>
            ))}
          </div>
        </div>
        {isChef && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => toggleChefRole.mutate({ 
              userId: user.id, 
              isChef: true
            })}
          >
            Revoke Chef Status
          </Button>
        )}
        {!isChef && user.roles.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => toggleChefRole.mutate({ 
              userId: user.id, 
              isChef: false
            })}
          >
            Make Chef
          </Button>
        )}
      </div>
    </Card>
  );

  return (
    <AdminLayout title="Users">
      <Tabs defaultValue="foodie" className="space-y-4">
        <TabsList>
          <TabsTrigger value="foodie">Foodies</TabsTrigger>
          <TabsTrigger value="chef">Chefs</TabsTrigger>
        </TabsList>

        <TabsContent value="foodie" className="space-y-4">
          {isLoading ? (
            <ListLoadingState />
          ) : foodieUsers && foodieUsers.length > 0 ? (
            foodieUsers.map((user) => (
              <UserCard key={user.id} user={user} isChef={false} />
            ))
          ) : (
            <p className="py-4 text-center text-muted-foreground">No foodie users found</p>
          )}
        </TabsContent>

        <TabsContent value="chef" className="space-y-4">
          {isLoading ? (
            <ListLoadingState />
          ) : chefUsers && chefUsers.length > 0 ? (
            chefUsers.map((user) => (
              <UserCard key={user.id} user={user} isChef={true} />
            ))
          ) : (
            <p className="py-4 text-center text-muted-foreground">No chef users found</p>
          )}
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};
